import {useState} from 'react';
import {Spinner} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import {MAX_VPN_ACCOUNTS, VpnAccount} from '../../Types/routerForms';
import {
  useAddVpnAccount,
  useGetVpn,
  useRemoveVpnAccount,
  useUpdateVpnPassword,
} from '../../Hooks/RouterManager';
import L2tpParamsSection from './L2tpParamsSection';
import VpnAccountsSection from './VpnAccountsSection';
import AddVpnAccountForm from './AddVpnAccountForm';
import EditVpnPasswordModal from './EditVpnPasswordModal';
import VpnSubnetSection from './VpnSubnetSection';
import VpnSecurityAdvice from './VpnSecurityAdvice';
import LoadingOverlay from '../../../../app/common/components/LoadingOverlay';

interface VpnL2tpPageProps {
  routerId: string;
}

const VpnL2tpPage = ({routerId}: VpnL2tpPageProps): JSX.Element => {
  const intl = useIntl();
  const {data: vpn, isLoading, isFetching} = useGetVpn(routerId);
  const [editing, setEditing] = useState<VpnAccount | null>(null);
  const addAccount = useAddVpnAccount(routerId);
  const removeAccount = useRemoveVpnAccount(routerId);
  const updatePassword = useUpdateVpnPassword(routerId);

  if (isLoading || !vpn) {
    return (
      <div className="text-center py-10">
        <Spinner animation="border" />
      </div>
    );
  }

  const handleAdd = (account: VpnAccount) =>
    addAccount.mutate({
      login: account.login,
      password: account.password,
      remoteAddress: account.remoteAddress,
    });
  const handleDelete = (id: string) => {
    if (window.confirm(intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.CONFIRM_DELETE'}))) {
      removeAccount.mutate(id);
    }
  };
  const handleSave = (account: VpnAccount) =>
    updatePassword.mutate({id: account.id, password: account.password});

  const atCapacity = vpn.accounts.length >= MAX_VPN_ACCOUNTS;

  return (
    <LoadingOverlay isLoading={isFetching}>
      <p className="text-muted mb-4">
        {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VPN.SUBTITLE'})}
      </p>

      <L2tpParamsSection routerId={routerId} params={vpn.l2tp} />
      <VpnAccountsSection accounts={vpn.accounts} onEdit={setEditing} onDelete={handleDelete} />
      <AddVpnAccountForm onAdd={handleAdd} disabled={atCapacity} />
      <VpnSubnetSection routerId={routerId} subnet={vpn.subnet} />
      <VpnSecurityAdvice />

      <EditVpnPasswordModal
        show={editing !== null}
        onHide={() => setEditing(null)}
        account={editing}
        onSave={handleSave}
      />
    </LoadingOverlay>
  );
};

export default VpnL2tpPage;
